import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type {
  EnvironmentKind,
  PolicyAction,
  PolicyMode,
  ProjectDetailDTO,
  ProjectLifecycle,
  ProjectMemberRole,
  ProjectPriority,
  UpdateProjectInput,
} from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, EmptyState, Field, Panel, SectionHeader, inputClass, toneColor, type Tone } from "@/design/primitives";
import { lifecycleLabel } from "@/design/project-visual";
import { canManage, useEnvironments, usePolicies, useProjectRole } from "../project-data";

const POLICY_LABELS: Record<PolicyAction, string> = {
  TASK_FROM_SCOPE: "AI vytváří tasky ze schváleného scope",
  PROPOSAL_AUTO_APPROVE: "Nová feature bez tvého schválení",
  KNOWLEDGE_AUTO_UPDATE: "AI samo aktualizuje Knowledge",
  BRANCH_CREATE: "Vytvořit branch",
  PR_CREATE: "Otevřít pull request",
  PR_MERGE: "Merge PR",
  SUPPORT_SEND: "Odeslat odpověď zákazníkovi",
};

const MODE_TONE: Record<PolicyMode, Tone> = { AUTO: "green", APPROVAL: "amber", DENIED: "red" };
const NEXT_MODE: Record<PolicyMode, PolicyMode> = {
  AUTO: "APPROVAL",
  APPROVAL: "DENIED",
  DENIED: "AUTO",
};

const DOD_DEFAULTS = ["unit testy", "integrační testy", "lint + typecheck", "build", "E2E kritických flow", "acceptance criteria ověřena"];

/** SETTINGS dle /Design: lifecycle/moduly/přístup · policies · environments/DoD/Constitution. */
export function ProjectSettingsTab({ project }: { project: ProjectDetailDTO }) {
  const role = useProjectRole(project);

  if (!canManage(role)) {
    return (
      <Panel className="mt-4 p-6">
        <EmptyState title={cs.common.forbidden} />
      </Panel>
    );
  }

  return (
    <div className="mt-4 grid grid-cols-1 items-start gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div className="flex flex-col gap-4">
        <LifecyclePanel project={project} />
        <MembersPanel project={project} />
      </div>
      <div className="flex flex-col gap-4">
        <PoliciesPanel project={project} />
        <DepartmentsPanel project={project} />
      </div>
      <div className="flex flex-col gap-4">
        <HostingServerPanel project={project} />
        <EnvironmentsPanel project={project} />
        <Panel className="p-4">
          <SectionHeader label={cs.projectSettings.dod} right={cs.projectSettings.dodNote} />
          <div className="mt-2">
            {DOD_DEFAULTS.map((d) => (
              <div key={d} className="flex gap-2 py-1 text-[11px]">
                <span style={{ color: "var(--positive)" }}>✓</span>
                <span style={{ color: "var(--text-primary)" }}>{d}</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel className="p-4" style={{ borderColor: "color-mix(in srgb, var(--critical) 20%, transparent)" }}>
          <div className="af-label mb-2" style={{ color: "var(--critical)" }}>
            {cs.projectSettings.constitution}
          </div>
          <div className="text-[11px] leading-[1.8]" style={{ color: "var(--text-secondary)" }}>
            {cs.projectSettings.constitutionText}
          </div>
        </Panel>
        {role === "OWNER" ? <DangerZonePanel project={project} /> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function LifecyclePanel({ project }: { project: ProjectDetailDTO }) {
  const queryClient = useQueryClient();
  const [lifecycle, setLifecycle] = useState<ProjectLifecycle>(project.lifecycle);
  const [priority, setPriority] = useState<ProjectPriority>(project.businessPriority);
  const [statusLine, setStatusLine] = useState(project.statusLine ?? "");
  const [reason, setReason] = useState("");
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (input: UpdateProjectInput) => api.updateProject(project.slug, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  return (
    <Panel className="p-4">
      <SectionHeader label={cs.projectSettings.lifecycle} />
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(
          ["DISCOVERY", "ACTIVE_DEV", "LAUNCH_PREP", "LIVE", "MAINTENANCE", "PAUSED", "ARCHIVED"] as const
        ).map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLifecycle(l)}
            className="cursor-pointer px-2.5 py-1 text-[11px] tracking-[0.08em]"
            style={{ borderRadius: "var(--radius-box)",
              border: `1px solid ${lifecycle === l ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
              color: lifecycle === l ? "var(--canvas)" : "var(--text-secondary)",
              background: lifecycle === l ? "var(--accent)" : "transparent",
            }}
            data-testid={`lifecycle-${l}`}
          >
            {lifecycleLabel[l]}
          </button>
        ))}
      </div>
      <p className="mt-2 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.project.lifecycleNote}
      </p>

      <div className="mt-4">
        <SectionHeader label={cs.projectSettings.businessPriority} />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(["LOW", "NORMAL", "HIGH", "CRITICAL"] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPriority(p)}
              className="cursor-pointer px-2.5 py-1 text-[11px] tracking-[0.1em]"
              style={{ borderRadius: "var(--radius-box)",
                border: `1px solid ${priority === p ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                color:
                  priority === p
                    ? p === "CRITICAL"
                      ? "var(--critical)"
                      : "var(--accent)"
                    : "var(--text-secondary)",
                background: priority === p ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent",
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Field label={cs.project.statusLine}>
          <input className={inputClass} value={statusLine} onChange={(e) => setStatusLine(e.target.value)} />
        </Field>
      </div>
      <div className="mt-3">
        <Field label={cs.project.reasonLabel}>
          <input
            className={inputClass}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="např. klient posunul launch"
          />
        </Field>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
        {saved ? (
          <span className="text-[12px]" style={{ color: "var(--positive)" }}>
            ✓ uloženo · zapsáno do auditu
          </span>
        ) : null}
        <Btn
          variant="primary"
          disabled={mutation.isPending}
          onClick={() =>
            mutation.mutate({
              lifecycle,
              businessPriority: priority,
              statusLine: statusLine.trim() === "" ? null : statusLine.trim(),
              ...(reason.trim() ? { reason: reason.trim() } : {}),
            })
          }
          data-testid="project-save"
        >
          {cs.common.save}
        </Btn>
      </div>
    </Panel>
  );
}


function MembersPanel({ project }: { project: ProjectDetailDTO }) {
  const queryClient = useQueryClient();
  const manages = canManage(useProjectRole(project));
  const usersQuery = useQuery({ queryKey: ["users"], queryFn: api.users, enabled: manages });
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedRole, setSelectedRole] = useState<ProjectMemberRole>("COLLABORATOR");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["projects", project.slug] });
  const addMember = useMutation({
    mutationFn: () => api.setProjectMember(project.slug, { userId: selectedUser, role: selectedRole }),
    onSuccess: invalidate,
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) => api.removeProjectMember(project.slug, userId),
    onSuccess: invalidate,
  });

  const candidates = (usersQuery.data?.users ?? []).filter(
    (u) => !u.disabled && u.role !== "OWNER" && !project.members.some((m) => m.userId === u.id),
  );

  return (
    <Panel className="p-4" data-testid="members-panel">
      <SectionHeader label={cs.projectSettings.access} />
      <div className="mt-2 flex flex-col gap-1.5">
        {project.members.length === 0 ? (
          <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            Zatím jen owner (globální přístup).
          </div>
        ) : (
          project.members.map((m) => (
            <div key={m.userId} className="flex items-center justify-between gap-3 text-[12px]">
              <span className="min-w-0 break-words" style={{ color: "var(--text-primary)" }}>{m.user.name}</span>
              <span className="flex flex-none items-center gap-3">
                <span className="af-label-dim">{m.role}</span>
                {manages ? (
                  <button
                    type="button"
                    className="cursor-pointer text-[11px]"
                    style={{ color: "var(--critical)" }}
                    onClick={() => removeMember.mutate(m.userId)}
                  >
                    ✕
                  </button>
                ) : null}
              </span>
            </div>
          ))
        )}
      </div>
      {manages && candidates.length > 0 ? (
        <div className="mt-3 flex flex-col gap-2 pt-3 sm:flex-row sm:items-end" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <Field label={cs.project.addMember}>
            <select
              className={inputClass}
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              style={{ background: "var(--surface-inset)" }}
              data-testid="member-select"
            >
              <option value="">—</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={cs.fields.role}>
            <select
              className={inputClass}
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as ProjectMemberRole)}
              style={{ background: "var(--surface-inset)" }}
            >
              <option value="MANAGER">MANAGER</option>
              <option value="COLLABORATOR">COLLABORATOR</option>
              <option value="CLIENT_VIEWER">CLIENT_VIEWER</option>
            </select>
          </Field>
          <Btn disabled={!selectedUser || addMember.isPending} onClick={() => addMember.mutate()} data-testid="member-add">
            +
          </Btn>
        </div>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function PoliciesPanel({ project }: { project: ProjectDetailDTO }) {
  const queryClient = useQueryClient();
  const policies = usePolicies(project.id);

  const mutation = useMutation({
    mutationFn: (input: { action: PolicyAction; mode: PolicyMode }) =>
      api.updatePolicies(project.id, { policies: { [input.action]: input.mode } }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["section", project.id, "policies"] }),
  });

  return (
    <Panel className="p-4">
      <SectionHeader label={cs.projectSettings.policies} />
      <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.projectSettings.policiesNote}
      </div>
      <div className="mt-2">
        {(policies.data?.policies ?? []).map((p) => (
          <div
            key={p.action}
            className="flex items-center justify-between gap-3 py-2 text-[11px]"
            style={{ borderTop: "1px solid var(--border-subtle)" }}
          >
            <span className="min-w-0" style={{ color: "var(--text-primary)" }}>{POLICY_LABELS[p.action]}</span>
            <button
              type="button"
              className="flex-none cursor-pointer px-2 py-0.5 text-[11px] tracking-[0.1em]"
              style={{ borderRadius: "var(--radius-box)",
                color: toneColor[MODE_TONE[p.mode]],
                border: `1px solid ${toneColor[MODE_TONE[p.mode]]}`,
                opacity: 0.9,
              }}
              onClick={() => mutation.mutate({ action: p.action, mode: NEXT_MODE[p.mode] })}
              title="Kliknutím přepneš AUTO → APPROVAL → DENIED"
              data-testid={`policy-${p.action}`}
            >
              {p.mode}
            </button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

/**
 * Oddělení projektu. API (`PUT /api/projects/:id/departments`) existovalo od
 * Fáze 3, ale v nastavení chybělo — projekt založený bez oddělení pak nešel
 * opravit a task s oddělením nešel založit.
 */
function DepartmentsPanel({ project }: { project: ProjectDetailDTO }) {
  const queryClient = useQueryClient();
  const all = useQuery({ queryKey: ["departments"], queryFn: api.departments });
  const selected = useQuery({
    queryKey: ["section", project.id, "departments"],
    queryFn: () => api.projectDepartments(project.id),
  });
  const selectedIds = new Set((selected.data?.departments ?? []).map((d) => d.id));

  const mutation = useMutation({
    mutationFn: (departmentIds: string[]) => api.setProjectDepartments(project.id, departmentIds),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["section", project.id, "departments"] }),
  });

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    mutation.mutate([...next]);
  };

  const rows = (all.data?.departments ?? []).filter((d) => d.enabled || selectedIds.has(d.id));

  return (
    <Panel className="p-4">
      <SectionHeader label={cs.projectSettings.departments} right={`${selectedIds.size}/${rows.length}`} />
      <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.projectSettings.departmentsNote}
      </div>
      <div className="mt-2">
        {rows.map((d) => {
          const on = selectedIds.has(d.id);
          const color = toneColor[(d.color as Tone) in toneColor ? (d.color as Tone) : "cyan"];
          return (
            <div
              key={d.id}
              className="flex items-center justify-between gap-3 py-2 text-[11px]"
              style={{ borderTop: "1px solid var(--border-subtle)" }}
            >
              <span className="min-w-0 break-words" style={{ color: on ? "var(--text-primary)" : "var(--text-secondary)" }}>
                {d.name}
              </span>
              <button
                type="button"
                className="flex-none cursor-pointer px-2 py-0.5 text-[11px] tracking-[0.1em]"
                style={{
                  borderRadius: "var(--radius-box)",
                  color: on ? color : "var(--text-tertiary)",
                  border: `1px solid ${on ? color : "var(--border-default)"}`,
                }}
                disabled={mutation.isPending || selected.isLoading}
                onClick={() => toggle(d.id)}
                data-testid={`settings-department-${d.key}`}
              >
                {on ? "✓ ZAPNUTO" : "+ ZAPNOUT"}
              </button>
            </div>
          );
        })}
        {!rows.length && !all.isLoading ? (
          <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.common.none}
          </div>
        ) : null}
      </div>
      {mutation.error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          {mutation.error.message}
        </div>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

/** Na kterém Coolify serveru projekt běží (vlastní server ownera, nebo server jiného uživatele). */
function HostingServerPanel({ project }: { project: ProjectDetailDTO }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["section", project.id, "hosting-server"],
    queryFn: () => api.projectHostingServer(project.id),
  });
  const environments = useEnvironments(project.id);
  // na jiném serveru staré Coolify ID neplatí — připomenout jen tam, kde nějaké jsou
  const hasCoolifyMapping = (environments.data?.environments ?? []).some(
    (e) => e.hostingProvider === "COOLIFY" && e.hostingProjectId,
  );
  const [saved, setSaved] = useState(false);
  const mutation = useMutation({
    mutationFn: (serverId: string | null) => api.setProjectHostingServer(project.id, serverId),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ["section", project.id] });
    },
  });
  const data = query.data;
  const currentId = data?.server?.id ?? "";
  // aktuální server, i když ho volající nevlastní (jinak by select ukázal nesmysl)
  const options = [...(data?.assignable ?? [])];
  if (data?.server && !options.some((o) => o.id === data.server!.id)) options.unshift(data.server);
  const canPick = !!data?.canChange && (data.assignable.length > 0 || !!data.server);

  return (
    <Panel className="p-4" data-testid="project-hosting-server">
      <SectionHeader label={cs.projectSettings.hostingServer} />
      <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.projectSettings.hostingServerNote}
      </div>
      <select
        className={`${inputClass} mt-3`}
        style={{ background: "var(--surface-inset)" }}
        value={currentId}
        disabled={!canPick || mutation.isPending || query.isLoading}
        onChange={(e) => {
          setSaved(false);
          mutation.mutate(e.target.value || null);
        }}
        data-testid="project-hosting-server-select"
      >
        <option value="">{cs.projectSettings.hostingServerDefault(data?.defaultServer?.name ?? null)}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name} — {o.apiUrl}
          </option>
        ))}
      </select>
      {saved ? (
        <div className="mt-2 text-[11px]" style={{ color: hasCoolifyMapping ? "var(--warning)" : "var(--positive)" }}>
          {hasCoolifyMapping ? cs.projectSettings.hostingServerChanged : cs.projectSettings.hostingServerSaved}
        </div>
      ) : null}
      {mutation.error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          {mutation.error.message}
        </div>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function EnvironmentsPanel({ project }: { project: ProjectDetailDTO }) {
  const queryClient = useQueryClient();
  const environments = useEnvironments(project.id);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<EnvironmentKind>("STAGING");

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["section", project.id, "environments"] });

  const createMutation = useMutation({
    mutationFn: () => api.createEnvironment(project.id, { name, kind, deployPolicy: "APPROVAL" }),
    onSuccess: () => {
      setAdding(false);
      setName("");
      void invalidate();
    },
  });
  const cycleMutation = useMutation({
    mutationFn: (input: { id: string; mode: PolicyMode }) =>
      api.updateEnvironment(input.id, { deployPolicy: input.mode }),
    onSuccess: invalidate,
  });
  const branchMutation = useMutation({
    mutationFn: (input: { id: string; branch: string | null }) =>
      api.updateEnvironment(input.id, { branch: input.branch }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteEnvironment(id),
    onSuccess: invalidate,
  });

  return (
    <Panel className="p-4" data-testid="environments-panel">
      <SectionHeader
        label={cs.projectSettings.environments}
        right={
          <button
            type="button"
            className="cursor-pointer"
            style={{ color: "var(--accent)" }}
            onClick={() => setAdding((v) => !v)}
          >
            {cs.projectSettings.addEnv}
          </button>
        }
      />
      <div className="mt-1">
        {(environments.data?.environments ?? []).map((e) => (
          <div
            key={e.id}
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2 text-[11px]"
            style={{ borderTop: "1px solid var(--border-subtle)" }}
          >
            <span className="min-w-0" style={{ color: "var(--text-primary)" }}>
              {e.name} <span style={{ color: "var(--text-tertiary)" }}>({e.kind})</span>
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <span title="Deploy větev — deploy na environment = dostat kód do této větve">
                <span style={{ color: "var(--text-tertiary)" }}>⎇</span>{" "}
                <input
                  className="w-[72px] bg-transparent text-[11px] outline-none"
                  style={{
                    color: "var(--text-secondary)",
                    borderBottom: "1px dashed var(--border-default)",
                  }}
                  defaultValue={e.branch ?? ""}
                  placeholder="větev"
                  onBlur={(ev) => {
                    const value = ev.target.value.trim();
                    if (value !== (e.branch ?? "")) {
                      branchMutation.mutate({ id: e.id, branch: value || null });
                    }
                  }}
                  data-testid={`env-branch-${e.key}`}
                />
              </span>
              <button
                type="button"
                className="cursor-pointer px-2 py-0.5 text-[11px] tracking-[0.1em]"
                style={{ borderRadius: "var(--radius-box)",
                  color: toneColor[MODE_TONE[e.deployPolicy]],
                  border: `1px solid ${toneColor[MODE_TONE[e.deployPolicy]]}`,
                  opacity: 0.9,
                }}
                title="Deploy policy — kliknutím přepneš"
                onClick={() => cycleMutation.mutate({ id: e.id, mode: NEXT_MODE[e.deployPolicy] })}
                data-testid={`env-policy-${e.key}`}
              >
                deploy {e.deployPolicy}
              </button>
              <button
                type="button"
                className="cursor-pointer text-[11px]"
                style={{ color: "var(--critical)" }}
                onClick={() => deleteMutation.mutate(e.id)}
              >
                ✕
              </button>
            </span>
          </div>
        ))}
        {(environments.data?.environments ?? []).length === 0 ? (
          <div className="py-2 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            Žádná environments — přidej staging/production.
          </div>
        ) : null}
      </div>
      {adding ? (
        <div className="mt-2 flex flex-col gap-2 pt-2 sm:flex-row sm:items-end" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <Field label={cs.fields.name}>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={cs.fields.type}>
            <select
              className={inputClass}
              style={{ background: "var(--surface-inset)" }}
              value={kind}
              onChange={(e) => setKind(e.target.value as EnvironmentKind)}
            >
              {(["LOCAL", "DEVELOPMENT", "STAGING", "PRODUCTION", "CUSTOM"] as const).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </Field>
          <Btn disabled={name.trim().length < 2 || createMutation.isPending} onClick={() => createMutation.mutate()}>
            +
          </Btn>
        </div>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

/** Nevratné smazání projektu — jen owner, potvrzení zadáním slugu. */
function DangerZonePanel({ project }: { project: ProjectDetailDTO }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [slugInput, setSlugInput] = useState("");

  const mutation = useMutation({
    mutationFn: () => api.deleteProject(project.slug),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      void navigate({ to: "/projects" });
    },
  });

  const close = () => {
    setConfirming(false);
    setSlugInput("");
  };

  return (
    <Panel className="p-4" style={{ borderColor: "color-mix(in srgb, var(--critical) 35%, transparent)" }} data-testid="danger-zone">
      <div className="af-label mb-2" style={{ color: "var(--critical)" }}>
        {cs.projectSettings.dangerZone}
      </div>
      <div className="mb-3 text-[11px] leading-[1.8]" style={{ color: "var(--text-secondary)" }}>
        {cs.projectSettings.deleteProjectNote}
      </div>
      <Btn variant="danger" onClick={() => setConfirming(true)} data-testid="project-delete">
        {cs.projectSettings.deleteProject}
      </Btn>

      {confirming ? (
        // portál do <body>: obsah stránky je v `af-animate-fade`, což je vlastní
        // vrstva — modál uvnitř by se nedostal nad rail
        createPortal(
          <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: "var(--surface-overlay)", backdropFilter: "blur(3px)" }}
          onClick={close}
          data-testid="project-delete-modal"
        >
          <Panel brackets className="w-[420px] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <span className="af-label" style={{ color: "var(--critical)" }}>
                {cs.projectSettings.deleteProject} · {project.name}
              </span>
              <button
                type="button"
                className="cursor-pointer text-[11px]"
                style={{ color: "var(--text-secondary)" }}
                onClick={close}
              >
                ✕
              </button>
            </div>
            <div className="mb-3 text-[11px] leading-[1.7]" style={{ color: "var(--text-secondary)" }}>
              {cs.projectSettings.deleteProjectNote}
            </div>
            <Field label={cs.projectSettings.deleteConfirmText(project.slug)}>
              <input
                className={inputClass}
                value={slugInput}
                onChange={(e) => setSlugInput(e.target.value)}
                placeholder={cs.projectSettings.deleteConfirmPlaceholder}
                autoFocus
                data-testid="project-delete-slug"
              />
            </Field>
            {mutation.isError ? (
              <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
                {mutation.error instanceof Error ? mutation.error.message : cs.common.error}
              </div>
            ) : null}
            <div className="mt-4 flex items-center justify-end gap-2">
              <Btn onClick={close}>{cs.common.cancel}</Btn>
              <Btn
                variant="danger"
                disabled={slugInput.trim().toLowerCase() !== project.slug || mutation.isPending}
                onClick={() => mutation.mutate()}
                data-testid="project-delete-confirm"
              >
                {cs.projectSettings.deleteConfirmAction}
              </Btn>
            </div>
          </Panel>
          </div>,
          document.body,
        )
      ) : null}
    </Panel>
  );
}
